import Icon from "@react-native-vector-icons/material-design-icons/static";
import type { ReactNode } from "react";
import { Text, TouchableRipple } from "react-native-paper";
import type { LevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { InfoChip } from "../InfoChip";
import { LevelIcon } from "../LevelIcon";

type RecommendedSessionCardChip = {
	icon: LevelIconName;
	label?: string;
	tooltip?: string;
};

type Props = {
	icon: LevelIconName;
	title: string;
	subtitle?: ReactNode;
	chips?: RecommendedSessionCardChip[];
	showUpgrade?: boolean;
	selected?: boolean;
	disabled?: boolean;
	onPress: () => void;
};

export const RecommendedSessionCard = ({
	icon,
	title,
	subtitle,
	chips,
	showUpgrade,
	selected,
	disabled,
	onPress,
}: Props) => {
	const theme = useAppTheme();

	return (
		<TouchableRipple
			onPress={onPress}
			disabled={disabled}
			borderless={false}
			accessibilityRole="button"
			accessibilityState={{ selected, disabled }}
			// @ts-ignore - hovered is a web-only property
			style={({ hovered }) => ({
				borderRadius: theme.roundness * 2,
				borderWidth: 1,
				borderColor: selected
					? theme.colors.secondary
					: theme.colors.surfaceVariant,
				backgroundColor:
					selected || hovered
						? theme.colors.elevation.level1
						: theme.colors.surface,
				overflow: "hidden",
				opacity: disabled ? 0.5 : 1,
			})}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				gap={8}
				paddingHorizontal={10}
				paddingVertical={16}
			>
				<Box
					flex={1}
					flexDirection="row"
					alignItems="center"
					gap={8}
					style={{ minWidth: 0 }}
				>
					<Box
						width={42}
						height={42}
						borderRadius={99}
						alignItems="center"
						justifyContent="center"
						backgroundColor={
							selected ? theme.colors.elevation.level5 : theme.colors.background
						}
					>
						<LevelIcon
							name={icon}
							size={24}
							color={selected ? theme.colors.secondary : theme.colors.onSurface}
						/>
					</Box>
					<Box flex={1} gap={4} style={{ minWidth: 0 }}>
						<Box flexDirection="row" alignItems="center" gap={4}>
							<Text
								variant="titleMedium"
								style={{ color: theme.colors.onSurface, fontWeight: "600" }}
							>
								{title}
							</Text>
							{showUpgrade ? (
								<Icon name="star" size={18} color={theme.colors.tertiary} />
							) : null}
						</Box>
						{subtitle ? (
							<Text
								variant="bodyMedium"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{subtitle}
							</Text>
						) : null}
					</Box>
				</Box>
				{chips?.length ? (
					<Box flexDirection="row" alignItems="center" gap={8}>
						{chips.map((chip) => (
							<InfoChip
								key={`${chip.icon}-${chip.label ?? ""}`}
								icon={chip.icon}
								label={chip.label}
								tooltip={chip.tooltip}
							/>
						))}
					</Box>
				) : null}
			</Box>
		</TouchableRipple>
	);
};
